import { useCallback, useRef, useEffect } from 'react';
import { debounce } from 'lodash';
import {
  V1LimitPriceType,
  V1PreviewOrderCostRequest,
  type V1PreviewOrderCostResponse,
  type TradeServiceApi,
} from '@fintronners/react-api/src';

type UsePreviewOrderCostOptions = {
  onSuccess?: (data: V1PreviewOrderCostResponse) => Promise<void> | void;
  onError?: (error: Error) => void;
  onRequestStart?: () => void;
  onRequestEnd?: () => void;
  onRequestInvoked?: () => void;
  onRequestSkipped?: () => void;
  withDebounce?: boolean;
  shouldSkip?: boolean;
  apiService: TradeServiceApi;
};

type UsePreviewOrderCostReturn = {
  getOrderCost: (
    params: UsePreviewOrderCostParams,
  ) => Promise<V1PreviewOrderCostResponse | Error | null> | void;
  abortGetOrderCost: () => void;
};

type UsePreviewOrderCostParams = {
  assetUid: string;
  portfolioUid: string;
  quantity: string;
  price: string;
  parValue: string;
};

const DEFAULT_DEBOUNCE_TIME = 500;

/**
 * Hook for previewing order costs
 *
 * @param options - Optional callbacks and configuration
 * @param options.onSuccess - Callback called when order cost is successfully fetched
 * @param options.onError - Callback called when an error occurs
 * @param options.onRequestStart - Callback called when the API request starts
 * @param options.onRequestEnd - Callback called when the API request ends
 * @param options.onRequestInvoked - Callback called when the API request is invoked
 * @param options.onRequestSkipped - Callback called when the API request is skipped
 * @param options.withDebounce - Whether to debounce the API calls. When enabled, loading state shows immediately on user input, but API call is delayed.
 * @param options.shouldSkip - Whether to skip the API call.
 * @param options.apiService - The trade API service to use.
 * @returns Object containing:
 * - getOrderCost: Function to fetch order cost preview with parameters
 */
const usePreviewOrderCost = ({
  onSuccess,
  onError,
  onRequestStart,
  onRequestEnd,
  onRequestInvoked,
  onRequestSkipped,
  withDebounce = false,
  shouldSkip = false,
  apiService,
}: UsePreviewOrderCostOptions): UsePreviewOrderCostReturn => {
  const tradeApiService = apiService;
  const abortControllerRef = useRef<AbortController | undefined>(undefined);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onRequestStartRef = useRef(onRequestStart);
  const onRequestEndRef = useRef(onRequestEnd);
  const onRequestInvokedRef = useRef(onRequestInvoked);
  const onRequestSkippedRef = useRef(onRequestSkipped);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onRequestStartRef.current = onRequestStart;
  }, [onRequestStart]);

  useEffect(() => {
    onRequestEndRef.current = onRequestEnd;
  }, [onRequestEnd]);

  useEffect(() => {
    onRequestInvokedRef.current = onRequestInvoked;
  }, [onRequestInvoked]);

  useEffect(() => {
    onRequestSkippedRef.current = onRequestSkipped;
  }, [onRequestSkipped]);

  const invokeRequest = useCallback(
    async ({ assetUid, portfolioUid, quantity, price, parValue }: UsePreviewOrderCostParams) => {
      onRequestStartRef.current?.();

      try {
        const body: V1PreviewOrderCostRequest = {
          portfolioUid,
          assetUid,
          quantity,
          limitPrice: {
            type: V1LimitPriceType.PercentageOfPar,
            price,
          },
          fixedIncome: {
            parValue,
          },
        };
        const { data: orderCostResponse } = await tradeApiService.tradeServicePreviewOrderCost(
          body,
          {
            signal: abortControllerRef.current?.signal,
          },
        );

        await onSuccessRef.current?.(orderCostResponse);
        return orderCostResponse;
      } catch (err) {
        if (err instanceof Error && (err.name === 'CanceledError' || err.name === 'AbortError')) {
          return null;
        }

        const errorToSet = err instanceof Error ? err : new Error('Unknown error occurred');

        onErrorRef.current?.(errorToSet);
        return errorToSet;
      } finally {
        onRequestEndRef.current?.();
        abortControllerRef.current = undefined;
      }
    },
    [tradeApiService],
  );

  const invokeDebouncedRequest = useCallback(
    debounce(invokeRequest, DEFAULT_DEBOUNCE_TIME, {
      leading: !withDebounce,
      trailing: withDebounce,
    }),
    [invokeRequest, withDebounce],
  );

  const abortRequest = useCallback(() => {
    const isPending = Boolean(abortControllerRef.current);

    if (!isPending) {
      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = undefined;
    invokeDebouncedRequest.cancel();
    onRequestEndRef.current?.();
  }, [invokeDebouncedRequest]);

  const getOrderCost = useCallback(
    (...args: Parameters<typeof invokeRequest>) => {
      abortRequest();

      if (shouldSkip) {
        onRequestSkippedRef.current?.();
        return;
      }

      onRequestInvokedRef.current?.();

      abortControllerRef.current = new AbortController();

      return invokeDebouncedRequest(...args);
    },
    [invokeDebouncedRequest, shouldSkip],
  );

  return {
    getOrderCost,
    abortGetOrderCost: abortRequest,
  };
};

export default usePreviewOrderCost;
